import { Spacing } from '@persona-id/design-tokens';
import { useMemo } from 'react';
import styled from 'styled-components';

import { RolePermission } from 'dashboard/models/role';
import {
  EXPORT_PERMISSION_NAMES,
  REDACTION_PERMISSION_NAMES,
} from 'dashboard/views/DashboardRolesShowView/constants';
import Card from 'design-system/components/Card';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { Permission } from 'lib/permissions';

export interface ProductsAdvancedSettingsProps {
  disabled: boolean;
  isAdmin: boolean;
  selectedEnvironmentIds: string[];
  rolePermissions: RolePermission[];
  setRolePermissions: (permissions: RolePermission[]) => void;
}

const ProductsAdvancedSettings = ({
  disabled,
  isAdmin,
  selectedEnvironmentIds,
  rolePermissions,
  setRolePermissions,
}: ProductsAdvancedSettingsProps) => {
  const possibleRolePermissionsForRedact = useMemo(() => {
    return possibleRolePermissionsFor({
      permissionNames: REDACTION_PERMISSION_NAMES,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      environmentIds: selectedEnvironmentIds.length > 0 ? selectedEnvironmentIds : [null],
    });
  }, [selectedEnvironmentIds]);

  const possibleRolePermissionsForExport = useMemo(() => {
    return possibleRolePermissionsFor({
      permissionNames: EXPORT_PERMISSION_NAMES,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      environmentIds: selectedEnvironmentIds.length > 0 ? selectedEnvironmentIds : [null],
    });
  }, [selectedEnvironmentIds]);

  const handleRedactCheckbox = () => {
    if (someRedactSelected) {
      setRolePermissions(
        rolePermissions.filter((rp) => !REDACTION_PERMISSION_NAMES.includes(rp.name)),
      );
    } else {
      setRolePermissions([...rolePermissions, ...possibleRolePermissionsForRedact]);
    }
  };

  const someRedactSelected = useMemo(() => {
    return rolePermissions.find((rp) => REDACTION_PERMISSION_NAMES.includes(rp.name)) != null;
  }, [rolePermissions]);

  const handleExportCheckbox = () => {
    if (someExportSelected) {
      setRolePermissions(
        rolePermissions.filter((rp) => !EXPORT_PERMISSION_NAMES.includes(rp.name)),
      );
    } else {
      setRolePermissions([...rolePermissions, ...possibleRolePermissionsForExport]);
    }
  };

  const someExportSelected = useMemo(() => {
    return rolePermissions.find((rp) => EXPORT_PERMISSION_NAMES.includes(rp.name)) != null;
  }, [rolePermissions]);

  return (
    <>
      <Card.SectionHeader>Advanced Settings</Card.SectionHeader>
      <div>
        <StyledRow>
          <StyledCheckboxCell>
            <Checkbox
              id='permissions-table__advanced-settings-redact-checkbox'
              checked={isAdmin || someRedactSelected}
              disabled={disabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRedactCheckbox();
              }}
            />
          </StyledCheckboxCell>
          <div>
            <StyledParagraph size='sm' color='primary'>
              Enable redaction for all products.
            </StyledParagraph>
            <Paragraph>Allows this role to redact all products</Paragraph>
          </div>
        </StyledRow>
        <Spacer height={2} />
        <StyledRow>
          <StyledCheckboxCell>
            <Checkbox
              id='permissions-table__advanced-settings-export-checkbox'
              checked={isAdmin || someExportSelected}
              disabled={disabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleExportCheckbox();
              }}
            />
          </StyledCheckboxCell>
          <div>
            <StyledParagraph size='sm' color='primary'>
              Enable export for all products.
            </StyledParagraph>
            <Paragraph>
              Allows this role to use the export, download PDF, and print features across all
              products
            </Paragraph>
          </div>
        </StyledRow>
      </div>
    </>
  );
};

export default ProductsAdvancedSettings;

const StyledParagraph = styled(Paragraph)`
  font-weight: 500;
`;

const StyledRow = styled.div`
  display: flex;
`;

const StyledCheckboxCell = styled.div`
  height: ${Spacing[6]};
  width: ${Spacing[7]};
  margin-top: 4px;
  margin-left: 6px;
`;

const possibleRolePermissionsFor = ({
  permissionNames,
  environmentIds,
}: {
  permissionNames: Permission[];
  environmentIds: string[];
}): RolePermission[] => {
  return environmentIds.flatMap((environmentId) => {
    return permissionNames.map((name) => {
      return {
        name,
        'environment-id': environmentId,
        'object-id': null,
      };
    });
  });
};
